/**
 * Service layer — all-inclusive fixed fee.
 *
 * A proposal normally derives every phase fee from Airtable's fee engine. Occasionally a client
 * asks for one all-inclusive number covering everything including the phases the firm normally
 * bills variably (Permitting, Bidding & Negotiation, Construction Support). This module holds the
 * manual total, the proportional back-fill, and the reads/writes for the override row.
 *
 * WHERE THE SPLIT MATH LIVES: here, once. The editor computes the per-phase dollars and SAVES
 * them; the server stores and serves them without ever re-deriving. That is deliberate — every
 * line is hand-editable after the initial split, so a server that recomputed from proportions
 * would silently discard the user's own adjustments.
 *
 * Backed by Supabase (`proposal_fee_overrides`) through /api/proposal-fee-override.
 * See database/241_proposal_fee_overrides.sql and docs/ALL_INCLUSIVE_FEE.md.
 */

/**
 * The phases an all-inclusive fee can cover, in document order.
 *
 * `proportionField` is the Airtable lookup on the Fee Proposal carrying the firm's own curated
 * share for that phase — read LIVE off the record so a re-curated fee model feeds the split
 * automatically, exactly as it feeds the engine.
 *
 * BN and CS have no such field: the Fee Models table carries proportions for the eight design
 * phases only (`BD Average Billing Rate` / `CS Average Billing Rate` are hourly ESTIMATING
 * inputs, not shares of a fee). Their proportions below are firm defaults set by Matt 2026-09-12
 * — Bidding 5%, Construction Support 15% — consistent with the proposal's own standing prose
 * ("Bidding... typically nominal", "Construction Support... estimated at 10-20% of the overall
 * fee"). They are editable per proposal; change these two numbers to move the firm-wide default.
 */
export const ALL_INCLUSIVE_PHASES = [
  { code: 'PD', label: 'Pre-design & Planning',   phase: 'Pre-design & Planning Phase',   proportionField: 'PD Proportion (from Fee Model)' },
  { code: 'SD', label: 'Schematic Design',        phase: 'Schematic Design Phase',        proportionField: 'SD Proportion (from Fee Model)', adjustKey: 'SD Adjustment' },
  { code: 'DA', label: 'Discretionary Approvals', phase: 'Discretionary Approvals Phase', proportionField: 'DA Proportion (from Fee Model)', adjustKey: 'DA Adjustment' },
  { code: 'DD', label: 'Design Development',      phase: 'Design Development Phase',      proportionField: 'DD Proportion (from Fee Model)' },
  { code: 'CD', label: 'Construction Documents',  phase: 'Construction Documents Phase',  proportionField: 'CD Proportion (from Fee Model)' },
  { code: 'PR', label: 'Permitting',              phase: 'Permitting Phase',              proportionField: 'PR Proportion (from Fee Model)', normallyVariable: true },
  { code: 'AD', label: 'Architectural Detailing', phase: 'Architectural Detailing Phase', proportionField: 'AD Proportion (from Fee Model)', adjustKey: 'AD Adjustment' },
  { code: 'IA', label: 'Interior Architecture',   phase: 'Interior Architecture Phase',   proportionField: 'IA Proportion (from Fee Model)', adjustKey: 'IA Adjustment' },
  // Not in `Phases Included` — every Basic Services project gets both, and the standard proposal
  // prices them hourly under "Construction Advancement".
  { code: 'BN', label: 'Bidding & Negotiation',   alwaysIncluded: true, defaultProportion: 0.05, normallyVariable: true },
  { code: 'CS', label: 'Construction Support',    alwaysIncluded: true, defaultProportion: 0.15, normallyVariable: true },
]

export const ALL_INCLUSIVE_CODES = ALL_INCLUSIVE_PHASES.map(p => p.code)

/**
 * The Construction Support site visits a fixed fee can include, in the order they happen.
 *
 * These are the phase's own scheduled WBS milestones, so the contract commits to the work the
 * project plan already contains rather than to a number written for the document. Construction
 * Closeout is a milestone of that phase but is deliberately absent — it is administrative, not a
 * site visit (Matt, 2026-09-12).
 *
 * KEEP IN SYNC with CS_SITE_VISITS in api/_proposalFeeOverride.js (a deliberate mirror — that file
 * is server-side and cannot be imported here). Editing this list never changes an issued contract:
 * the visits a proposal commits to are frozen onto its own row at save.
 */
export const CS_SITE_VISITS = [
  'Construction Kick-Off',
  'Foundation & Layout Review',
  'Framing & Spatial Review',
  'Rough-In / Pre-Cover Review',
  'Envelope & Waterproofing Review',
  'Finish Layout & Mock-Up Review',
  'Substantial Completion',
]

/** Bidding & Negotiation's own prose already describes two meetings: the contractor walk-through
 *  and the bid review with the client. */
export const BN_DEFAULT_MEETINGS = 2

/** Permitting, counted as TWO separate allowances (Matt, 2026-09-12) — a public hearing and a
 *  staff call are not the same commitment. This allowance also covers Discretionary Approvals,
 *  which defers its own meetings to the Permitting Phase. */
export const PR_DEFAULT_HEARINGS = 2
export const PR_DEFAULT_MEETINGS = 4

/** The three phases a standard proposal bills variably — what "all-inclusive" actually buys. */
export const NORMALLY_VARIABLE_CODES = ALL_INCLUSIVE_PHASES.filter(p => p.normallyVariable).map(p => p.code)

function num(v) { const n = Number(Array.isArray(v) ? v[0] : v); return isNaN(n) ? 0 : n }

/**
 * Which phases an all-inclusive fee covers: whatever the proposal sells (`Phases Included`), plus
 * Bidding & Negotiation and Construction Support, which every Basic Services project carries and
 * which this fee is specifically absorbing.
 *
 * `phasesIncluded` may be passed directly — the EDITOR passes its live, on-screen selection rather
 * than the saved record, because the two differ the moment someone changes the phase list before
 * saving, and a split computed against the stale set prices phases the client is not buying and
 * misses ones they are. (That is exactly what went wrong on 2000 Santiago Drive: Interior
 * Architecture was priced at $24,990 while Discretionary Approvals, which the proposal actually
 * sells, got nothing.)
 */
export function includedCodesFor(proposalRaw, phasesIncluded) {
  const included = Array.isArray(phasesIncluded) ? phasesIncluded
    : (Array.isArray(proposalRaw?.['Phases Included']) ? proposalRaw['Phases Included'] : [])
  return ALL_INCLUSIVE_PHASES
    .filter(p => p.alwaysIncluded || included.includes(p.phase))
    .map(p => p.code)
}

/**
 * The firm's EFFECTIVE share for each phase — the same weight Airtable's own engine uses.
 *
 * A phase's fee is not its raw fee-model proportion: four phases (SD/DA/AD/IA) carry a per-proposal
 * Adjustment that the engine multiplies straight into the fee
 * (`<X> Fee = ROUND({Weighted Fee Amount} * {<X> Proportion} * {<X> Adjustment}, -1)`), and those
 * adjustments are routinely far from 100% — 2000 Santiago Drive runs SD at 90%, Discretionary
 * Approvals at 50%, Architectural Detailing at 95%. Splitting a manual total by the raw proportions
 * ignores the firm's own judgement about how much each phase is worth ON THIS PROJECT, which is the
 * whole reason those adjustments exist.
 *
 * `adjustments` is an optional live map of FRACTIONS ({ SD: 0.9 }) from the editor's own form;
 * without it the saved record's values are used. A phase with no adjustment field weighs 1.
 *
 * Returns { PD: 0.02, SD: 0.216, ... } for the codes asked for.
 */
export function proportionsFor(proposalRaw, codes, adjustments) {
  const out = {}
  for (const p of ALL_INCLUSIVE_PHASES) {
    if (!codes.includes(p.code)) continue
    const base = p.proportionField ? num(proposalRaw?.[p.proportionField]) : 0
    const share = base > 0 ? base : (p.defaultProportion || 0)
    let adj = 1
    if (p.adjustKey) {
      const live = adjustments && adjustments[p.code]
      const raw = live != null ? live : proposalRaw?.[p.adjustKey]
      // A missing adjustment means "no adjustment" (weigh 1). A real 0 means the firm priced this
      // phase at nothing, which is a legitimate answer and must stay 0 — so test for null/'' , not
      // for falsiness.
      adj = (raw == null || raw === '') ? 1 : num(raw)
    }
    out[p.code] = Math.round(share * adj * 1e6) / 1e6
  }
  return out
}

/**
 * Does a saved override still match what the proposal sells?
 *
 * Bidding & Negotiation and Construction Support are excluded from the comparison: they are never
 * in `Phases Included`, and leaving them out of an all-inclusive fee is a legitimate choice (they
 * simply stay hourly). Every DESIGN phase, though, must be on both sides — a phase sold but not
 * priced silently falls back to the fee engine's own number, and a phase priced but not sold hides
 * money in a line the client never sees. Either way the rows stop adding up to the quoted total.
 *
 * Returns { ok, missing, extra } with phase CODES.
 */
export function overrideDrift(override, includedCodes) {
  if (!override || !override.phaseFees) return { ok: true, missing: [], extra: [] }
  const design = c => !ALL_INCLUSIVE_PHASES.find(p => p.code === c)?.alwaysIncluded
  const covered = Object.keys(override.phaseFees)
  const missing = includedCodes.filter(c => design(c) && !covered.includes(c))
  const extra = covered.filter(c => design(c) && !includedCodes.includes(c))
  return { ok: !missing.length && !extra.length, missing, extra }
}

/** Phase label for a code, for error and warning copy. */
export function phaseLabel(code) {
  const p = ALL_INCLUSIVE_PHASES.find(x => x.code === code)
  return p ? p.label : code
}

/**
 * Divide a manual total across phases by their proportions.
 *
 * The firm's proportions do NOT sum to 1 across an arbitrary set — PD through AD sum to exactly
 * 1.00 (the fee model's own `Basic Phases Total %`), then Permitting (12%) and Interiors (35%) sit
 * on top of it, and Bidding/Construction Support on top of that. So every share is normalized
 * against the sum of the INCLUDED proportions rather than used as a raw fraction of the total.
 *
 * Rounds each phase to $10 the way Airtable's own `<X> Fee` formulas do (`ROUND(..., -1)`), then
 * puts the rounding remainder on the largest phase so the parts equal the whole to the cent. An
 * all-inclusive fee whose rows do not add up to its own headline number is the one outcome this
 * feature cannot ship — it is the client's contract price and becomes real BQE Core phase
 * amounts at execution.
 */
export function splitFee(total, proportions) {
  const codes = Object.keys(proportions).filter(c => proportions[c] > 0)
  const t = Number(total) || 0
  if (!t || !codes.length) return {}
  const denom = codes.reduce((a, c) => a + proportions[c], 0)
  if (!(denom > 0)) return {}

  const out = {}
  for (const c of codes) out[c] = Math.round((t * proportions[c] / denom) / 10) * 10

  // Absorb the rounding drift on the biggest line, where a $10-$40 shift is least visible.
  const largest = codes.reduce((best, c) => (out[c] > out[best] ? c : best), codes[0])
  const drift = t - codes.reduce((a, c) => a + out[c], 0)
  out[largest] = Math.round((out[largest] + drift) * 100) / 100
  // A tiny total could push the largest line negative; that is a bad split, not a rounding issue.
  if (out[largest] < 0) return Object.fromEntries(codes.map(c => [c, Math.round((t * proportions[c] / denom) * 100) / 100]))

  // Phases the proposal includes at no charge stay in scope at $0 — they are covered by the fee,
  // which is a different statement from "not sold".
  for (const c of Object.keys(proportions)) if (!(c in out)) out[c] = 0
  return out
}

/** Sum of a { code: dollars } map. */
export function sumFees(phaseFees) {
  return Object.values(phaseFees || {}).reduce((a, b) => a + (Number(b) || 0), 0)
}

/**
 * Pin one phase to a new amount and push the difference onto the others, keeping the total exact.
 *
 * This is the lever for the judgement the firm's own percentages cannot make. The fee model's
 * proportions describe how a STANDARD fee divides, where Permitting is not in the fixed fee at all
 * — its share is an estimate of hourly work sitting outside the pot. Fold everything into one
 * all-inclusive number and that share suddenly competes with the design phases for a fixed sum,
 * which is not what it was calibrated to do. Moving it down and letting SD/DD/CD absorb the
 * difference is a pricing call, so the tool makes it a one-field edit instead of arithmetic.
 *
 * The remainder is spread across the other phases by their CURRENT relative weights, so previous
 * hand-tuning is preserved in proportion rather than reset. Rounds to $10 like the engine, and the
 * rounding remainder lands on the largest of the others so the parts still equal the whole.
 * A phase already at 0 stays at 0 — it has no weight to receive a share.
 */
export function rebalanceAround(phaseFees, total, code, newFee) {
  const t = Number(total) || 0
  const pinned = Math.max(0, Math.min(t, Math.round((Number(newFee) || 0) / 10) * 10))
  const others = Object.keys(phaseFees).filter(c => c !== code)
  if (!others.length) return { ...phaseFees, [code]: t }

  const out = { ...phaseFees, [code]: pinned }
  const remainder = t - pinned
  const weightOf = c => Number(phaseFees[c]) || 0
  let denom = others.reduce((a, c) => a + weightOf(c), 0)

  // Every other phase sits at 0 (or this is the first move): fall back to an even spread, since
  // there are no relative weights left to preserve.
  const even = !(denom > 0)
  if (even) denom = others.length

  for (const c of others) {
    const w = even ? 1 : weightOf(c)
    out[c] = Math.round((remainder * w / denom) / 10) * 10
  }

  const largest = others.reduce((best, c) => (out[c] > out[best] ? c : best), others[0])
  const drift = t - others.reduce((a, c) => a + out[c], 0) - pinned
  out[largest] = Math.round((out[largest] + drift) * 100) / 100
  if (out[largest] < 0) out[largest] = 0
  return out
}

/**
 * Push the difference between the phases and the headline fee onto a chosen set of phases.
 *
 * The counterpart to typing a share by hand. `rebalanceAround` fires on EVERY share edit, which
 * makes setting several in a row self-defeating: pin Permitting at 10%, then set Schematic Design,
 * and Permitting moves off the 10% just chosen. So share edits are now free, and this is the
 * explicit "make it add up" step — spread across the phases that have NOT been hand-set, by their
 * current relative weights, so a deliberate number stays where it was put.
 *
 * Rounds to $10 like the engine and lands the drift on the largest receiver, so the parts equal
 * the whole exactly. With nothing left to receive (every phase pinned, or all receivers at zero)
 * it spreads evenly rather than refusing — the alternative is a button that silently does nothing.
 */
export function spreadRemainder(phaseFees, total, acrossCodes) {
  const t = Number(total) || 0
  const codes = (acrossCodes || []).filter(c => c in phaseFees)
  if (!t || !codes.length) return { ...phaseFees }

  const out = { ...phaseFees }
  const others = Object.keys(out).filter(c => !codes.includes(c))
  const fixed = others.reduce((a, c) => a + (Number(out[c]) || 0), 0)
  const toSpread = t - fixed
  if (toSpread < 0) return { ...phaseFees }   // the pinned lines alone already exceed the fee

  const weightOf = c => Number(phaseFees[c]) || 0
  let denom = codes.reduce((a, c) => a + weightOf(c), 0)
  const even = !(denom > 0)
  if (even) denom = codes.length

  for (const c of codes) out[c] = Math.round((toSpread * (even ? 1 : weightOf(c)) / denom) / 10) * 10

  const largest = codes.reduce((best, c) => (out[c] > out[best] ? c : best), codes[0])
  const drift = t - Object.keys(out).reduce((a, c) => a + (Number(out[c]) || 0), 0)
  out[largest] = Math.round((out[largest] + drift) * 100) / 100
  if (out[largest] < 0) out[largest] = 0
  return out
}

/**
 * What each phase's dollars are as a share of the whole fee — the number that actually matters
 * when reading the split, and the one a reader compares against the fee model's own percentages.
 *
 * NOT the same as the model proportion: the model's shares are normalized across whatever set is
 * included, so a phase carrying a 20% model proportion out of a 123% total is 16.3% of the money.
 * Showing the raw proportion beside the dollars invites exactly that misreading.
 */
export function shareOfTotal(fee, total) {
  const t = Number(total) || 0
  if (!t) return 0
  return (Number(fee) || 0) / t
}

/** The model's own weights normalized across the included set, so they are directly comparable to
 *  `shareOfTotal` — this is what the split WOULD be with no hand-tuning. */
export function standardShares(proportions) {
  const codes = Object.keys(proportions)
  const denom = codes.reduce((a, c) => a + (proportions[c] || 0), 0)
  const out = {}
  for (const c of codes) out[c] = denom > 0 ? proportions[c] / denom : 0
  return out
}

/* ── API ──────────────────────────────────────────────────────────────────── */

async function api(action, params = {}) {
  const token = await window.__clerkGetToken()
  const res = await fetch('/api/proposal-fee-override', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify({ action, ...params }),
  }).then(r => r.json())
  if (res.error) throw new Error(res.error)
  return res
}

/** The override for one proposal, or null when it prices normally. */
export async function getFeeOverride(proposalId) {
  if (!proposalId) return null
  const { override } = await api('get', { proposalId })
  return override
}

/** Store the manual total and its resolved per-phase split. The server checks they match. */
export async function saveFeeOverride(proposalId, { totalFee, phaseFees, proportions, note, bnMeetings, csVisits, prHearings, prMeetings }) {
  const { override } = await api('save', { proposalId, totalFee, phaseFees, proportions, note, bnMeetings, csVisits, prHearings, prMeetings })
  return override
}

/** Return the proposal to the standard fee engine. Airtable never stopped computing underneath. */
export async function clearFeeOverride(proposalId) {
  await api('clear', { proposalId })
  return null
}
